import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import type { AppError } from '../../api/contract';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { countActiveFilterValues } from '../../shared/filter/activeFilterCount';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { bangkokWallTimeToRfc3339 } from '../commission-ledger/types';
import {
  defaultExceptionQuery,
  defaultRunQuery,
  reconciliationRepositoryToken,
} from './repository';
import type {
  CreateReconciliationRunInput,
  ReconciliationExceptionFilters,
  ReconciliationExceptionQuery,
  ReconciliationException,
  ReconciliationRun,
  ReconciliationRunFilters,
  ReconciliationRunQuery,
} from './types';

export function ReconciliationPage({ dataSource }: { readonly dataSource: DataSource }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<'runs' | 'exceptions'>(() =>
    searchParams.get('tab') === 'exceptions' ? 'exceptions' : 'runs',
  );
  const { t } = useTranslation();
  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source={dataSource} />}
        subtitle={t('reconciliation.subtitle')}
        title={t('reconciliation.title')}
      />
      <Tabs
        aria-label={t('reconciliation.tabs.label')}
        onChange={(_event, value: 'runs' | 'exceptions') => {
          setTab(value);
          const next = new URLSearchParams(searchParams);
          if (value === 'exceptions') next.set('tab', value);
          else next.delete('tab');
          setSearchParams(next, { replace: true });
        }}
        value={tab}
        sx={{ mb: 2 }}
      >
        <Tab label={t('reconciliation.tabs.runs')} value="runs" />
        <Tab label={t('reconciliation.tabs.exceptions')} value="exceptions" />
      </Tabs>
      {tab === 'runs' ? (
        <RunList dataSource={dataSource} searchParams={searchParams} />
      ) : (
        <ExceptionList searchParams={searchParams} />
      )}
    </Box>
  );
}

function RunList({
  dataSource,
  searchParams,
}: {
  readonly dataSource: DataSource;
  readonly searchParams: URLSearchParams;
}) {
  const repository = useRepository(reconciliationRepositoryToken);
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [query, setQuery] = useState(() => initialRunQuery(searchParams));
  const [history, setHistory] = useState<readonly string[]>(['']);
  const [page, setPage] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const resource = useResource(
    (signal) => repository.listRuns(query, signal),
    [query, refresh, repository],
    (value) => value.items.length === 0,
  );
  const reset = (filters: ReconciliationRunFilters) => {
    setHistory(['']);
    setPage(0);
    setQuery({ ...query, cursor: '', filters });
  };
  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ justifyContent: 'space-between' }}
        spacing={1}
      >
        <RunFilters value={query.filters} onApply={reset} />
        <Button onClick={() => setCreateOpen(true)} variant="contained">
          {t('reconciliation.actions.createRun')}
        </Button>
      </Stack>
      <PageState
        emptyMessage={t('reconciliation.emptyRuns')}
        error={safePageError(resource.error, t('reconciliation.errors.loadRuns'))}
        onRetry={() => setRefresh((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <>
            {mobile ? (
              <CompactRunResults items={resource.data.items} language={language} />
            ) : (
              <TableRegion label={t('reconciliation.runsTable')}>
                <table aria-label={t('reconciliation.runsTable')}>
                  <thead>
                    <tr>
                      <th>{t('reconciliation.fields.id')}</th>
                      <th>{t('reconciliation.fields.type')}</th>
                      <th>{t('reconciliation.fields.phase')}</th>
                      <th>{t('reconciliation.fields.partner')}</th>
                      <th>{t('reconciliation.fields.checked')}</th>
                      <th>{t('reconciliation.fields.exceptions')}</th>
                      <th>{t('reconciliation.fields.requestedAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resource.data.items.map((run) => (
                      <tr key={run.id}>
                        <td>
                          <Link to={`${paths.reconciliation}/runs/${encodeURIComponent(run.id)}`}>
                            {run.id}
                          </Link>
                        </td>
                        <td>
                          {run.type} · {run.trigger}
                        </td>
                        <td>{run.phase}</td>
                        <td>{run.partner?.code ?? t('reconciliation.allPartners')}</td>
                        <td>{run.checkedCount}</td>
                        <td>{run.exceptionCount}</td>
                        <td>
                          {formatTimestamp(run.requestedAt, 'Asia/Bangkok', language).display}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableRegion>
            )}
            {resource.data.requestId === null ? null : (
              <Typography variant="caption">
                {t('reconciliation.fields.requestId')}: {resource.data.requestId}
              </Typography>
            )}
            <CursorActions
              page={page}
              hasNext={resource.data.nextCursor !== null}
              onPrevious={() => {
                const index = page - 1;
                setPage(index);
                setQuery((current) => ({ ...current, cursor: history[index] ?? '' }));
              }}
              onNext={() => {
                const cursor = resource.data?.nextCursor;
                if (cursor === null || cursor === undefined) return;
                const index = page + 1;
                setHistory((current) => [...current.slice(0, index), cursor]);
                setPage(index);
                setQuery((current) => ({ ...current, cursor }));
              }}
            />
          </>
        )}
      </PageState>
      <CreateRunDialog
        dataSource={dataSource}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          setRefresh((value) => value + 1);
        }}
        open={createOpen}
      />
    </Stack>
  );
}

function ExceptionList({ searchParams }: { readonly searchParams: URLSearchParams }) {
  const repository = useRepository(reconciliationRepositoryToken);
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [query, setQuery] = useState(() => initialExceptionQuery(searchParams));
  const [history, setHistory] = useState<readonly string[]>(['']);
  const [page, setPage] = useState(0);
  const resource = useResource(
    (signal) => repository.listExceptions(query, signal),
    [query, repository],
    (value) => value.items.length === 0,
  );
  const reset = (filters: ReconciliationExceptionFilters) => {
    setHistory(['']);
    setPage(0);
    setQuery({ ...query, cursor: '', filters });
  };
  return (
    <Stack spacing={2}>
      <ExceptionFilters value={query.filters} onApply={reset} />
      <PageState
        emptyMessage={t('reconciliation.emptyExceptions')}
        error={safePageError(resource.error, t('reconciliation.errors.loadExceptions'))}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <>
            {mobile ? (
              <CompactExceptionResults items={resource.data.items} language={language} />
            ) : (
              <TableRegion label={t('reconciliation.exceptionsTable')}>
                <table aria-label={t('reconciliation.exceptionsTable')}>
                  <thead>
                    <tr>
                      <th>{t('reconciliation.fields.id')}</th>
                      <th>{t('reconciliation.fields.type')}</th>
                      <th>{t('reconciliation.fields.severity')}</th>
                      <th>{t('reconciliation.fields.entity')}</th>
                      <th>{t('reconciliation.fields.status')}</th>
                      <th>{t('reconciliation.fields.lastDetected')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resource.data.items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <Link
                            to={`${paths.reconciliation}/exceptions/${encodeURIComponent(item.id)}`}
                          >
                            {item.id}
                          </Link>
                        </td>
                        <td>{item.type}</td>
                        <td>{item.severity}</td>
                        <td>
                          {item.entityType} · {item.entityId}
                        </td>
                        <td>{item.status}</td>
                        <td>
                          {formatTimestamp(item.lastDetectedAt, 'Asia/Bangkok', language).display}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableRegion>
            )}
            {resource.data.requestId === null ? null : (
              <Typography variant="caption">
                {t('reconciliation.fields.requestId')}: {resource.data.requestId}
              </Typography>
            )}
            <CursorActions
              page={page}
              hasNext={resource.data.nextCursor !== null}
              onPrevious={() => {
                const index = page - 1;
                setPage(index);
                setQuery((current) => ({ ...current, cursor: history[index] ?? '' }));
              }}
              onNext={() => {
                const cursor = resource.data?.nextCursor;
                if (cursor === null || cursor === undefined) return;
                const index = page + 1;
                setHistory((current) => [...current.slice(0, index), cursor]);
                setPage(index);
                setQuery((current) => ({ ...current, cursor }));
              }}
            />
          </>
        )}
      </PageState>
    </Stack>
  );
}

function CompactRunResults({
  items,
  language,
}: {
  readonly items: readonly ReconciliationRun[];
  readonly language: TimeLanguage;
}) {
  const { t } = useTranslation();
  return (
    <Stack
      aria-label={t('reconciliation.compactRuns')}
      component="ul"
      spacing={1}
      sx={{ listStyle: 'none', m: 0, p: 0 }}
    >
      {items.map((run) => (
        <Paper component="li" key={run.id} sx={{ p: 2 }} variant="outlined">
          <Stack spacing={0.75}>
            <Link to={`${paths.reconciliation}/runs/${encodeURIComponent(run.id)}`}>{run.id}</Link>
            <Typography variant="body2">
              {run.type} · {run.trigger} · {run.phase}
            </Typography>
            <Typography variant="body2">
              {run.partner?.code ?? t('reconciliation.allPartners')}
            </Typography>
            <Typography variant="body2">
              {t('reconciliation.fields.checked')}: {run.checkedCount} ·{' '}
              {t('reconciliation.fields.exceptions')}: {run.exceptionCount}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {formatTimestamp(run.requestedAt, 'Asia/Bangkok', language).display}
            </Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function CompactExceptionResults({
  items,
  language,
}: {
  readonly items: readonly ReconciliationException[];
  readonly language: TimeLanguage;
}) {
  const { t } = useTranslation();
  return (
    <Stack
      aria-label={t('reconciliation.compactExceptions')}
      component="ul"
      spacing={1}
      sx={{ listStyle: 'none', m: 0, p: 0 }}
    >
      {items.map((item) => (
        <Paper component="li" key={item.id} sx={{ p: 2 }} variant="outlined">
          <Stack spacing={0.75}>
            <Link to={`${paths.reconciliation}/exceptions/${encodeURIComponent(item.id)}`}>
              {item.id}
            </Link>
            <Typography variant="body2">
              {item.type} · {item.severity} · {item.status}
            </Typography>
            <Typography variant="body2">
              {item.entityType} · {item.entityId}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {formatTimestamp(item.lastDetectedAt, 'Asia/Bangkok', language).display}
            </Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function RunFilters({
  value,
  onApply,
}: {
  readonly value: ReconciliationRunFilters;
  readonly onApply: (filters: ReconciliationRunFilters) => void;
}) {
  const initialValue = useRef(value);
  const { register, handleSubmit, reset } = useForm<ReconciliationRunFilters>({
    defaultValues: initialValue.current,
  });
  const { t } = useTranslation();
  return (
    <ListFilterPanel
      ariaLabel={t('filterPanel.label', { page: t('reconciliation.tabs.runs') })}
      onClear={() => {
        reset(defaultRunQuery.filters);
        onApply(defaultRunQuery.filters);
      }}
      onSubmit={handleSubmit(onApply)}
      primaryFields={
        <>
          <TextField
            defaultValue={initialValue.current.type}
            label={t('reconciliation.fields.type')}
            select
            size="small"
            {...register('type')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="WALLET_BALANCE">WALLET_BALANCE</MenuItem>
            <MenuItem value="WITHDRAWAL_CREDIT">WITHDRAWAL_CREDIT</MenuItem>
          </TextField>
          <TextField
            defaultValue={initialValue.current.trigger}
            label={t('reconciliation.fields.trigger')}
            select
            size="small"
            {...register('trigger')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="AUTOMATED">AUTOMATED</MenuItem>
            <MenuItem value="MANUAL">MANUAL</MenuItem>
          </TextField>
          <TextField
            defaultValue={initialValue.current.phase}
            label={t('reconciliation.fields.phase')}
            select
            size="small"
            {...register('phase')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {['QUEUED', 'RUNNING', 'COMPLETED', 'FAILED'].map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('reconciliation.fields.partnerId')}
            size="small"
            {...register('partnerId')}
          />
          <TextField
            label={t('reconciliation.fields.from')}
            size="small"
            type="datetime-local"
            {...register('requestedFrom')}
          />
          <TextField
            label={t('reconciliation.fields.to')}
            size="small"
            type="datetime-local"
            {...register('requestedTo')}
          />
        </>
      }
      sx={{ flex: 1, mb: 0 }}
    />
  );
}

function ExceptionFilters({
  value,
  onApply,
}: {
  readonly value: ReconciliationExceptionFilters;
  readonly onApply: (filters: ReconciliationExceptionFilters) => void;
}) {
  const initialValue = useRef(value);
  const { register, handleSubmit, reset } = useForm<ReconciliationExceptionFilters>({
    defaultValues: initialValue.current,
  });
  const { t } = useTranslation();
  const activeSecondaryFilterCount = countActiveFilterValues(
    {
      entityType: value.entityType,
      partnerId: value.partnerId,
      detectedFrom: value.detectedFrom,
      detectedTo: value.detectedTo,
    },
    {
      entityType: defaultExceptionQuery.filters.entityType,
      partnerId: defaultExceptionQuery.filters.partnerId,
      detectedFrom: defaultExceptionQuery.filters.detectedFrom,
      detectedTo: defaultExceptionQuery.filters.detectedTo,
    },
  );
  return (
    <ListFilterPanel
      activeSecondaryFilterCount={activeSecondaryFilterCount}
      ariaLabel={t('filterPanel.label', { page: t('reconciliation.tabs.exceptions') })}
      onClear={() => {
        reset(defaultExceptionQuery.filters);
        onApply(defaultExceptionQuery.filters);
      }}
      onSubmit={handleSubmit((values) =>
        onApply({
          ...values,
          type: normalizeCode(values.type),
          entityType: normalizeCode(values.entityType),
        }),
      )}
      primaryFields={
        <>
          <TextField
            label={t('reconciliation.fields.entityId')}
            size="small"
            {...register('entityId')}
          />
          <TextField
            defaultValue={initialValue.current.status}
            label={t('reconciliation.fields.status')}
            select
            size="small"
            {...register('status')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="OPEN">OPEN</MenuItem>
            <MenuItem value="ACKNOWLEDGED">ACKNOWLEDGED</MenuItem>
            <MenuItem value="RESOLVED">RESOLVED</MenuItem>
          </TextField>
          <TextField
            defaultValue={initialValue.current.severity}
            label={t('reconciliation.fields.severity')}
            select
            size="small"
            {...register('severity')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="HIGH">HIGH</MenuItem>
            <MenuItem value="CRITICAL">CRITICAL</MenuItem>
          </TextField>
          <TextField label={t('reconciliation.fields.type')} size="small" {...register('type')} />
        </>
      }
      secondaryFields={
        <>
          <TextField
            label={t('reconciliation.fields.entityType')}
            size="small"
            {...register('entityType')}
          />
          <TextField
            label={t('reconciliation.fields.partnerId')}
            size="small"
            {...register('partnerId')}
          />
          <TextField
            label={t('reconciliation.fields.from')}
            size="small"
            type="datetime-local"
            {...register('detectedFrom')}
          />
          <TextField
            label={t('reconciliation.fields.to')}
            size="small"
            type="datetime-local"
            {...register('detectedTo')}
          />
        </>
      }
    />
  );
}

function CreateRunDialog({
  dataSource,
  open,
  onClose,
  onCreated,
}: {
  readonly dataSource: DataSource;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onCreated: () => void;
}) {
  const repository = useRepository(reconciliationRepositoryToken);
  const { register, handleSubmit, reset } = useForm<CreateReconciliationRunInput>({
    defaultValues: { type: 'WALLET_BALANCE', partnerId: '', reason: '' },
  });
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const submitting = useRef(false);
  const key = useRef(crypto.randomUUID());
  const { t } = useTranslation();
  const submit = handleSubmit(async (values) => {
    if (submitting.current) return;
    submitting.current = true;
    setError(null);
    try {
      await repository.createRun(values, key.current);
      key.current = crypto.randomUUID();
      reset();
      onCreated();
    } catch (caught) {
      setError(safePageError(caught, t('reconciliation.errors.createRun')));
    } finally {
      setTimeout(() => {
        submitting.current = false;
      }, 0);
    }
  });
  return (
    <Dialog aria-describedby="create-run-impact" onClose={onClose} open={open}>
      <DialogTitle>{t('reconciliation.actions.createRun')}</DialogTitle>
      <DialogContent>
        <Stack component="form" id="create-run-form" onSubmit={submit} spacing={2} sx={{ pt: 1 }}>
          <DataSourceBadge source={dataSource} />
          <Typography id="create-run-impact">{t('reconciliation.createImpact')}</Typography>
          {error === null ? null : (
            <Alert severity="error">
              {error.message}
              {error.requestId === null ? null : ` Request ID: ${error.requestId}`}
            </Alert>
          )}
          <TextField
            defaultValue="WALLET_BALANCE"
            label={t('reconciliation.fields.type')}
            select
            {...register('type', { required: true })}
          >
            <MenuItem value="WALLET_BALANCE">WALLET_BALANCE</MenuItem>
            <MenuItem value="WITHDRAWAL_CREDIT">WITHDRAWAL_CREDIT</MenuItem>
          </TextField>
          <TextField
            label={t('reconciliation.fields.partnerIdOptional')}
            {...register('partnerId')}
          />
          <TextField
            label={t('reconciliation.fields.reason')}
            multiline
            required
            {...register('reason', { required: true, maxLength: 2000 })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
        <Button form="create-run-form" type="submit" variant="contained">
          {t('reconciliation.actions.queueRun')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TableRegion({
  children,
  label,
}: {
  readonly children: React.ReactNode;
  readonly label: string;
}) {
  return (
    <Box
      aria-label={label}
      role="region"
      tabIndex={0}
      sx={{
        overflowX: 'auto',
        '& table': { borderCollapse: 'collapse', minWidth: 900, width: '100%' },
        '& th, & td': { borderBottom: 1, borderColor: 'divider', p: 1, textAlign: 'left' },
      }}
    >
      {children}
    </Box>
  );
}
function CursorActions({
  page,
  hasNext,
  onPrevious,
  onNext,
}: {
  readonly page: number;
  readonly hasNext: boolean;
  readonly onPrevious: () => void;
  readonly onNext: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
      <Button disabled={page === 0} onClick={onPrevious}>
        {t('reconciliation.pagination.previous')}
      </Button>
      <Button disabled={!hasNext} onClick={onNext}>
        {t('reconciliation.pagination.next')}
      </Button>
    </Stack>
  );
}

function initialRunQuery(params: URLSearchParams): ReconciliationRunQuery {
  const type = allow(params.get('type'), ['WALLET_BALANCE', 'WITHDRAWAL_CREDIT'] as const);
  const trigger = allow(params.get('trigger'), ['AUTOMATED', 'MANUAL'] as const);
  const phase = allow(params.get('phase'), ['QUEUED', 'RUNNING', 'COMPLETED', 'FAILED'] as const);
  return {
    ...defaultRunQuery,
    filters: {
      type,
      trigger,
      phase,
      partnerId: validUuid(params.get('partnerId')),
      requestedFrom: validTime(params.get('requestedFrom')),
      requestedTo: validTime(params.get('requestedTo')),
    },
  };
}

function initialExceptionQuery(params: URLSearchParams): ReconciliationExceptionQuery {
  return {
    ...defaultExceptionQuery,
    filters: {
      status: allow(params.get('status'), ['OPEN', 'ACKNOWLEDGED', 'RESOLVED'] as const),
      severity: allow(params.get('severity'), ['HIGH', 'CRITICAL'] as const),
      type: normalizeCode(params.get('type') ?? ''),
      entityType: normalizeCode(params.get('entityType') ?? ''),
      entityId: validUuid(params.get('entityId')),
      partnerId: validUuid(params.get('partnerId')),
      detectedFrom: validTime(params.get('detectedFrom')),
      detectedTo: validTime(params.get('detectedTo')),
    },
  };
}

function allow<const Value extends string>(
  value: string | null,
  values: readonly Value[],
): Value | '' {
  return value !== null && values.includes(value as Value) ? (value as Value) : '';
}
function normalizeCode(value: string) {
  const code = value.trim().toUpperCase();
  return /^[A-Z][A-Z0-9_]{1,99}$/u.test(code) ? code : '';
}
function validTime(value: string | null) {
  if (
    value === null ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?(?:Z|[+-]\d{2}:\d{2})?$/u.test(value)
  )
    return '';
  try {
    formatTimestamp(bangkokWallTimeToRfc3339(value));
    return value;
  } catch {
    return '';
  }
}

function validUuid(value: string | null) {
  return value !== null &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu.test(value.trim())
    ? value.trim()
    : '';
}

export function safePageError(
  error: unknown,
  fallback: string,
  knownMessages: Readonly<Record<string, string>> = {},
): Pick<AppError, 'message' | 'requestId'> | null {
  if (error === null || error === undefined) return null;
  const candidate = typeof error === 'object' && error !== null ? (error as Partial<AppError>) : {};
  return {
    message:
      typeof candidate.code === 'string' ? (knownMessages[candidate.code] ?? fallback) : fallback,
    requestId: typeof candidate.requestId === 'string' ? candidate.requestId : null,
  };
}
